/**
 * Team Cycle — full-screen "one team at a time" carousel. Click / arrow-key /
 * space-to-toggle-autoplay through teams. Shows large logo, budget, max bid,
 * and full player list with photos in one screen. Suitable for LED.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Trophy } from "@phosphor-icons/react";
import { resolveImageUrl } from "@/components/ImageInput";
import { PHOTO_FALLBACK, usePublicAuction, brandLogoFor } from "./_shared";

export function OverlayTeamCycle() {
  const { auctionId } = useParams();
  const { auction, teams, players } = usePublicAuction(auctionId);
  const [idx, setIdx] = useState(0);
  const [autoplay, setAutoplay] = useState(false);

  const settings = auction?.settings || {};
  const basePrice = settings.default_base_price || 10;
  const minPlayers = settings.min_players_per_team || 11;

  const teamRows = useMemo(() => teams.map((t) => {
    const bought = players.filter((p) => p.sold_to === t.id);
    const slotsAfter = Math.max(0, minPlayers - bought.length - 1);
    const maxBid = Math.max(0, (t.remaining_budget || 0) - slotsAfter * basePrice);
    return { ...t, bought, max_bid: maxBid };
  }), [teams, players, basePrice, minPlayers]);

  useEffect(() => {
    if (!autoplay || teamRows.length === 0) return;
    const id = setInterval(() => setIdx((v) => (v + 1) % teamRows.length), 8000);
    return () => clearInterval(id);
  }, [autoplay, teamRows.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") setIdx((v) => (v + 1) % Math.max(1, teamRows.length));
      if (e.key === "ArrowLeft") setIdx((v) => (v - 1 + teamRows.length) % Math.max(1, teamRows.length));
      if (e.key === " ") { e.preventDefault(); setAutoplay((v) => !v); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [teamRows.length]);

  const t = teamRows[idx];
  if (!t) return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center label-mono" data-testid="overlay-team-cycle-empty">
      No teams yet.
    </div>
  );
  const pct = t.budget ? Math.round(((t.budget - t.remaining_budget) / t.budget) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-emerald-950 text-white p-4 sm:p-8 relative" data-testid="overlay-team-cycle">
      <div className="flex items-center justify-between mb-4">
        <img src={brandLogoFor(auction)} alt="" className="h-10" />
        <div className="label-mono">
          Team {idx + 1} / {teamRows.length} · ← → to switch · SPACE to {autoplay ? "pause" : "auto-play"}
        </div>
        <button
          type="button"
          onClick={() => setAutoplay((v) => !v)}
          className="btn-ghost text-xs"
          data-testid="team-cycle-autoplay-toggle"
        >
          {autoplay ? "Pause" : "Autoplay"}
        </button>
      </div>

      <div
        className="rounded-xl border p-6 mb-6 flex items-center gap-6"
        style={{ borderColor: `${t.color}80`, background: `linear-gradient(90deg, ${t.color}22, transparent)` }}
        data-testid={`team-cycle-header-${t.id}`}
      >
        {t.logo_url ? (
          <img src={resolveImageUrl(t.logo_url)} alt="" className="w-28 h-28 rounded object-cover border-2" style={{ borderColor: t.color }} />
        ) : (
          <div className="w-28 h-28 grid place-items-center rounded" style={{ background: `${t.color}22`, border: `2px solid ${t.color}` }}>
            <Trophy weight="fill" size={56} style={{ color: t.color }} />
          </div>
        )}
        <div className="flex-1">
          <div className="label-mono">TEAM · {t.short_name}</div>
          <div className="font-display uppercase text-6xl leading-none">{t.name}</div>
          <div className="mt-2 text-lg text-zinc-300">Owner {t.owner_name || "—"}</div>
        </div>
        <div className="text-right">
          <div className="label-mono">REMAINING</div>
          <div className="font-display text-6xl text-emerald-400 leading-none">{(t.remaining_budget || 0).toLocaleString()}</div>
          <div className="label-mono mt-2">MAX BID · <span className="text-amber-300 font-bold">{t.max_bid.toLocaleString()}</span></div>
        </div>
      </div>

      <div className="h-2 bg-zinc-800 rounded overflow-hidden mb-6">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: t.color }} />
      </div>

      <div className="mb-4 flex items-center justify-between">
        <div className="label-mono">SQUAD ({t.bought.length}/{t.max_players || 15})</div>
        <div className="label-mono">Spent {(t.budget - t.remaining_budget).toLocaleString()} of {(t.budget || 0).toLocaleString()}</div>
      </div>
      {t.bought.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded p-16 text-center text-zinc-500">
          No players purchased yet.
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3">
          {t.bought.map((p) => (
            <div key={p.id} className="bg-black/60 border border-zinc-800 rounded overflow-hidden" data-testid={`team-cycle-player-${p.id}`}>
              <img
                src={p.photo_url ? resolveImageUrl(p.photo_url) : PHOTO_FALLBACK}
                alt={p.name}
                className="w-full aspect-[3/4] object-cover"
                onError={(e) => { e.target.src = PHOTO_FALLBACK; }}
              />
              <div className="p-2 text-xs">
                <div className="font-semibold truncate">{p.name}</div>
                <div className="label-mono">{p.role}</div>
                <div className="text-emerald-400 font-mono mt-0.5">{p.sold_price} pts</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex gap-1 z-20">
        {teamRows.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-emerald-400 w-6" : "bg-zinc-700 hover:bg-zinc-500"}`}
            data-testid={`team-cycle-dot-${i}`}
            aria-label={`Show team ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
